import React, { useEffect, useState } from 'react';
import { Card, Button, Modal, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2'; 

import Wrapper from '../../components/Wrapper';
import InquiriesTable from '../../components/tables/InquiriesTable';
import axiosInstance from '../../utils/axios';
import SoilDoctorLogo from '../../assets/Images/SoilDoctorLogo.png';
import { PARTNER_CATEGORIES } from '../../config/permissions.config';

const Inquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [partnerCreds, setPartnerCreds] = useState(null);
  const [modalChecked, setModalChecked] = useState(false);

  // 1. Fetch Inquiries
  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/Inquiry?source=partner&status=A');
      
      setInquiries(Array.isArray(data) ? data : data.data || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  // 2. Update Status
  const updateStatus = async (id, status) => {
    await axiosInstance.put(`/Inquiry/${id}`, { status });
  };

  // 3. Handle Reject
  const handleReject = (id) => {
    Swal.fire({
      title: 'Reject Inquiry?',
      text: "This action simply marks the inquiry as rejected.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, Reject'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await updateStatus(id, 'rejected');
          Swal.fire('Rejected!', 'Inquiry has been rejected.', 'success');
          fetchInquiries();
        } catch (err) {
          Swal.fire('Error', 'Failed to update status', 'error');
        }
      }
    });
  };

  // 4. Handle Accept
  const handleAccept = (row) => {
    Swal.fire({
      title: 'Accept & Create Partner?',
      text: `This will create an Admin account for ${row.entity_name}. Choose its partner category:`,
      icon: 'info',
      input: 'select',
      inputOptions: {
        '': 'Uncategorized',
        ...Object.fromEntries(PARTNER_CATEGORIES.map(({ value, label }) => [value, label])),
      },
      inputValue: '',
      showCancelButton: true,
      confirmButtonText: 'Yes, Create Admin'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const adminRes = await axiosInstance.post('/super-admin/AddAdmin', {
            inquiry_id: row.inquiry_id,
            category: result.value || null,
          });

          const adminData = adminRes.data;
          let rawAdmin = null;

          if (Array.isArray(adminData) && adminData.length > 0) {
            rawAdmin = adminData[0];
          } else if (adminData && adminData.username) {
            rawAdmin = adminData;
          }

          if (rawAdmin) {
            await updateStatus(row.inquiry_id, 'accepted'); 

            setPartnerCreds({
              username: rawAdmin.username,
              password: rawAdmin.password
            });
            setShowModal(true);
            fetchInquiries();
          } else {
            throw new Error("Admin creation failed (No creds returned)");
          }

        } catch (err) {
          console.error(err);
          Swal.fire('Error', err.response?.data?.msg || 'Operation failed', 'error');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleModalClose = () => {
    setShowModal(false);
    setPartnerCreds(null);
    setModalChecked(false);
  };

  return (
    <Wrapper>
      <div className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="fw-bold m-0">Partner Inquiries</h2>
          <Button variant="light" onClick={fetchInquiries} disabled={loading}>
            Refresh
          </Button>
        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-0">
            {loading && inquiries.length === 0 ? (
               <div className="text-center p-5"><Spinner animation="border"/></div>
            ) : (
               <InquiriesTable 
                  data={inquiries} 
                  onAccept={handleAccept} 
                  onReject={handleReject} 
               />
            )}
          </Card.Body>
        </Card>

        {/* CREDENTIALS MODAL */}
        <Modal show={showModal} backdrop="static" centered>
            <Modal.Header>
              <Modal.Title className="d-flex align-items-center gap-2">
                <img src={SoilDoctorLogo} alt="logo" width="120" />
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <h4 className="mb-3 text-success">Partner Created Successfully!</h4>
              <p className="text-muted">
                The inquiry has been accepted and an Admin account created.
                Please share these credentials securely:
              </p>
              
              <div className="bg-light p-3 rounded border mb-3">
                <div className="mb-2">
                    <span className="fw-bold text-secondary">Username:</span> <br/>
                    <span className="user-select-all fs-5">{partnerCreds?.username}</span>
                </div>
                <div>
                    <span className="fw-bold text-secondary">Password:</span> <br/>
                    <span className="user-select-all fs-5 text-dark fw-bold">{partnerCreds?.password}</span>
                </div>
              </div>

              <div className="form-check mt-3">
                <input
                    className="form-check-input"
                    type="checkbox"
                    id="ackCheck"
                    checked={modalChecked}
                    onChange={() => setModalChecked(!modalChecked)}
                /> 
                <label className="form-check-label" htmlFor="ackCheck">
                    I have noted down the credentials.
                </label>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="dark"
                disabled={!modalChecked}
                onClick={handleModalClose}
              >
                Close
              </Button>
            </Modal.Footer>
        </Modal>

      </div>
    </Wrapper>
  );
};

export default Inquiries;